import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Table,
  Input,
  Select,
  DatePicker,
  Button,
  Space,
  Tag,
  Tooltip,
  notification,
  Drawer,
  Descriptions,
  Typography,
  message,
  Spin,
  Empty,
} from 'antd';
import {
  ReloadOutlined,
  SearchOutlined,
  EyeOutlined,
  SendOutlined,
  CopyOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import lib from '../../../assets/utils/lib';
import {
  SAASPRO_OUTBOX_STATUS_COLORS,
  truncateMiddle,
} from '../../../components/SaaSPro/SaaSProAdvisoryShared';

const { RangePicker } = DatePicker;
const { Text, Paragraph } = Typography;

const EVENT_TYPE_OPTIONS = [
  { value: 'all', label: 'All event types' },
  { value: 'pa.submitted', label: 'pa.submitted' },
  { value: 'pa.approved', label: 'pa.approved' },
  { value: 'pa.rejected', label: 'pa.rejected' },
  { value: 'pa.queried', label: 'pa.queried' },
  { value: 'pa.flagged', label: 'pa.flagged' },
  { value: 'pa.unflagged', label: 'pa.unflagged' },
  { value: 'pa.decided', label: 'pa.decided' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'sent', label: 'Sent' },
  { value: 'failed', label: 'Failed' },
  { value: 'shadow', label: 'Shadow' },
];

const fmtDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString();
};

const copyToClipboard = async (value) => {
  if (!value) return;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(String(value));
    } else {
      const ta = document.createElement('textarea');
      ta.value = String(value);
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    message.success('Copied');
  } catch (e) {
    message.error('Copy failed');
  }
};

const OutboxTab = ({ partnerId, onJumpToDecisions }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    event_type: 'all',
    q: '',
    checkin_id: '',
    range: null,
  });
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [activeEventId, setActiveEventId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [redispatching, setRedispatching] = useState(false);

  const buildQuery = useCallback(() => {
    const q = {
      page: pagination.page,
      pageSize: pagination.pageSize,
    };
    if (filters.status && filters.status !== 'all') q.status = filters.status;
    if (filters.event_type && filters.event_type !== 'all') q.event_type = filters.event_type;
    if (filters.q?.trim()) q.q = filters.q.trim();
    if (filters.checkin_id?.trim()) q.checkin_id = filters.checkin_id.trim();
    if (filters.range && filters.range[0]) q.from = filters.range[0].toISOString();
    if (filters.range && filters.range[1]) q.to = filters.range[1].toISOString();
    return q;
  }, [filters, pagination.page, pagination.pageSize]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await lib.getPaAgentOutbox(partnerId, buildQuery());
      const body = resp?.data || resp;
      const data = body?.data || body;
      const list = Array.isArray(data?.rows)
        ? data.rows
        : Array.isArray(data)
          ? data
          : (Array.isArray(data?.items) ? data.items : []);
      const pag = body?.pagination || data?.pagination || {};
      const total = Number(pag.total ?? data?.total ?? list.length) || 0;
      setRows(list);
      setPagination((p) => ({ ...p, total }));
    } catch (e) {
      notification.error({ message: 'Failed to load outbox', description: e?.message });
    } finally {
      setLoading(false);
    }
  }, [buildQuery, partnerId]);

  useEffect(() => {
    if (!partnerId) return;
    fetchRows();
  }, [fetchRows, partnerId]);

  const openDetail = useCallback(async (eventId) => {
    setActiveEventId(eventId);
    setDetail(null);
    setDetailLoading(true);
    try {
      const resp = await lib.getPaAgentOutboxDetail(partnerId, eventId);
      const body = resp?.data || resp;
      if (Number(body?.error) || resp?.status === 'error') {
        notification.error({ message: body?.message || 'Could not load event' });
        setActiveEventId(null);
        return;
      }
      setDetail(body?.data || body);
    } catch (e) {
      notification.error({ message: 'Could not load event', description: e?.message });
      setActiveEventId(null);
    } finally {
      setDetailLoading(false);
    }
  }, [partnerId]);

  const closeDetail = useCallback(() => {
    setActiveEventId(null);
    setDetail(null);
  }, []);

  const handleRedispatch = useCallback(async () => {
    if (!activeEventId) return;
    setRedispatching(true);
    try {
      const resp = await lib.redispatchPaAgentEvent(partnerId, activeEventId);
      const body = resp?.data || resp;
      if (Number(body?.error) || resp?.status === 'error') {
        notification.error({ message: body?.message || 'Redispatch failed' });
        return;
      }
      notification.success({ message: 'Redispatch queued' });
      await fetchRows();
      await openDetail(activeEventId);
    } catch (e) {
      notification.error({ message: 'Redispatch failed', description: e?.message });
    } finally {
      setRedispatching(false);
    }
  }, [activeEventId, partnerId, fetchRows, openDetail]);

  const columns = useMemo(() => [
    {
      title: 'Created',
      dataIndex: 'created_at',
      width: 170,
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      defaultSortOrder: 'descend',
      render: (v) => <span style={{ fontSize: 12 }}>{fmtDateTime(v)}</span>,
    },
    {
      title: 'Event id',
      dataIndex: 'event_id',
      width: 200,
      render: (v) => (
        <Tooltip title={v}>
          <code style={{ fontSize: 12 }}>{truncateMiddle(v, 6, 6)}</code>
        </Tooltip>
      ),
    },
    {
      title: 'Checkin id',
      dataIndex: 'checkin_id',
      width: 160,
      render: (v) => (
        <Button
          type="link"
          size="small"
          icon={<LinkOutlined />}
          style={{ padding: 0 }}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/preauth-action?checkin=${encodeURIComponent(v)}`);
          }}
        >
          {v}
        </Button>
      ),
    },
    {
      title: 'Rev',
      dataIndex: 'submission_revision',
      width: 60,
      render: (v) => <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>{v ?? '--'}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (v) => (
        <Tag color={SAASPRO_OUTBOX_STATUS_COLORS[v] || 'default'} style={{ marginInlineEnd: 0 }}>
          {v}
        </Tag>
      ),
    },
    {
      title: 'Attempts',
      dataIndex: 'attempts',
      width: 90,
      render: (v) => (
        <span style={{ fontWeight: Number(v) > 1 ? 700 : 400 }}>{v ?? 0}</span>
      ),
    },
    {
      title: 'HTTP',
      dataIndex: 'http_status',
      width: 80,
      render: (v) => v ?? '',
    },
    {
      title: 'Sent at',
      dataIndex: 'sent_at',
      width: 170,
      render: (v) => <span style={{ fontSize: 12 }}>{v ? fmtDateTime(v) : ''}</span>,
    },
    {
      title: 'Last error',
      dataIndex: 'last_error',
      ellipsis: true,
      render: (v) => v ? (
        <Tooltip title={v}>
          <span style={{ color: '#a8071a', fontSize: 12 }}>{v}</span>
        </Tooltip>
      ) : null,
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, row) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={(e) => { e.stopPropagation(); openDetail(row.event_id); }}
        />
      ),
    },
  ], [navigate, openDetail]);

  const payloadString = useMemo(() => {
    if (!detail?.payload) return '';
    try {
      return JSON.stringify(detail.payload, null, 2);
    } catch (e) {
      return String(detail.payload);
    }
  }, [detail]);

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search checkin, event id, error…"
          style={{ width: 280 }}
          value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          onPressEnter={() => { setPagination((p) => ({ ...p, page: 1 })); fetchRows(); }}
        />
        <Select
          style={{ width: 140 }}
          value={filters.status}
          onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
          options={STATUS_OPTIONS}
        />
        <Select
          style={{ width: 170 }}
          value={filters.event_type}
          onChange={(v) => setFilters((f) => ({ ...f, event_type: v }))}
          options={EVENT_TYPE_OPTIONS}
        />
        <Input
          allowClear
          placeholder="Checkin id"
          style={{ width: 200 }}
          value={filters.checkin_id}
          onChange={(e) => setFilters((f) => ({ ...f, checkin_id: e.target.value }))}
          onPressEnter={() => { setPagination((p) => ({ ...p, page: 1 })); fetchRows(); }}
        />
        <RangePicker
          showTime
          value={filters.range}
          onChange={(value) => setFilters((f) => ({ ...f, range: value }))}
        />
        <Button
          type="primary"
          onClick={() => { setPagination((p) => ({ ...p, page: 1 })); fetchRows(); }}
        >
          Apply
        </Button>
        <Button icon={<ReloadOutlined />} onClick={fetchRows}>Refresh</Button>
      </Space>

      <Table
        size="small"
        rowKey={(row) => row?.event_id || row?.id}
        columns={columns}
        dataSource={rows}
        loading={loading}
        onRow={(row) => ({ onClick: () => openDetail(row.event_id) })}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          onChange: (page, pageSize) => setPagination({ ...pagination, page, pageSize }),
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span style={{ color: 'rgba(0,0,0,0.55)' }}>
                  No outbox events match these filters. PA submissions auto-enqueue here.
                </span>
              }
            />
          ),
        }}
      />

      <Drawer
        title={detail ? (
          <Space>
            <Tag color={SAASPRO_OUTBOX_STATUS_COLORS[detail.status] || 'default'}>{detail.status}</Tag>
            <code style={{ fontSize: 13 }}>{detail.event_id}</code>
          </Space>
        ) : 'Outbox event'}
        width={680}
        open={Boolean(activeEventId)}
        onClose={closeDetail}
        destroyOnClose
        extra={
          <Space>
            <Button
              icon={<CopyOutlined />}
              onClick={() => copyToClipboard(detail?.event_id)}
              disabled={!detail?.event_id}
            >
              Copy id
            </Button>
            <Button
              icon={<LinkOutlined />}
              onClick={() => detail?.checkin_id && navigate(`/preauth-action?checkin=${encodeURIComponent(detail.checkin_id)}`)}
              disabled={!detail?.checkin_id}
            >
              Encounter
            </Button>
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={redispatching}
              onClick={handleRedispatch}
              disabled={!detail}
            >
              Redispatch
            </Button>
          </Space>
        }
      >
        {detailLoading ? (
          <Spin />
        ) : detail ? (
          <>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="Checkin id">
                {detail.checkin_id ? (
                  <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={() => navigate(`/preauth-action?checkin=${encodeURIComponent(detail.checkin_id)}`)}
                  >
                    {detail.checkin_id}
                  </Button>
                ) : '--'}
              </Descriptions.Item>
              <Descriptions.Item label="Revision">{detail.submission_revision ?? '--'}</Descriptions.Item>
              <Descriptions.Item label="Event type">{detail.event_type || '--'}</Descriptions.Item>
              <Descriptions.Item label="Attempts">{detail.attempts ?? 0}</Descriptions.Item>
              <Descriptions.Item label="HTTP">{detail.http_status ?? '--'}</Descriptions.Item>
              <Descriptions.Item label="Sent at">{detail.sent_at ? fmtDateTime(detail.sent_at) : '--'}</Descriptions.Item>
              <Descriptions.Item label="Last error">
                {detail.last_error ? <Text type="danger">{detail.last_error}</Text> : '--'}
              </Descriptions.Item>
              <Descriptions.Item label="Payload size">{detail.payload_size_bytes ?? '--'} bytes</Descriptions.Item>
              <Descriptions.Item label="Outbound auth preview">
                {detail.signature_preview ? (
                  <code style={{ fontSize: 12 }}>{detail.signature_preview}</code>
                ) : (
                  <Text type="secondary">Bearer token (legacy HMAC preview empty)</Text>
                )}
              </Descriptions.Item>
            </Descriptions>

            <div style={{ marginTop: 16 }}>
              <Space style={{ marginBottom: 6 }}>
                <Text strong>Payload</Text>
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(payloadString)}
                  disabled={!payloadString}
                >
                  Copy JSON
                </Button>
                {onJumpToDecisions && detail.event_id && (
                  <Button
                    size="small"
                    type="link"
                    onClick={() => onJumpToDecisions({ correlation_id: detail.event_id, checkin_id: detail.checkin_id })}
                  >
                    See decisions for this event
                  </Button>
                )}
              </Space>
              <Paragraph
                style={{
                  background: '#0b1021',
                  color: '#dbe4ff',
                  borderRadius: 6,
                  padding: 12,
                  maxHeight: 360,
                  overflow: 'auto',
                  fontFamily: 'ui-monospace,Menlo,Consolas,monospace',
                  fontSize: 12,
                  whiteSpace: 'pre',
                }}
              >
                {payloadString || '(empty)'}
              </Paragraph>
            </div>
          </>
        ) : null}
      </Drawer>
    </div>
  );
};

export default OutboxTab;
